/**
 * Horizontally-scrolling carousel of every category tile, intended to live
 * just below the hero. Lets first-time visitors scan the breadth of the
 * directory before they scroll. Auto-snaps and shows nav arrows on hover.
 */
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CATEGORY_IMAGES } from "@/lib/categoryImages";
import { slugForCategory } from "@/lib/categoryHeroes";

const ALL_CATEGORIES = Object.keys(CATEGORY_IMAGES);

export default function CategoryRail() {
  const scrollerRef = useRef(null);

  const scrollBy = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  return (
    <section
      className="bg-tbc-navy text-white py-8 lg:py-12"
      data-testid="category-rail"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5 lg:mb-7">
          <div>
            <p className="text-tbc-gold text-xs uppercase tracking-[0.25em] font-medium mb-1">
              Browse the community
            </p>
            <h2 className="font-heading text-2xl lg:text-3xl">
              Every category, one tap away
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-400)}
              aria-label="Scroll left"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-tbc-gold hover:text-tbc-navy transition-colors flex items-center justify-center"
              data-testid="category-rail-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(400)}
              aria-label="Scroll right"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-tbc-gold hover:text-tbc-navy transition-colors flex items-center justify-center"
              data-testid="category-rail-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 lg:-mx-8 lg:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollPadding: "0 16px" }}
        >
          {ALL_CATEGORIES.map((category) => {
            const heroSlug = slugForCategory(category);
            const to = heroSlug
              ? `/category/${heroSlug}`
              : `/directory?category=${encodeURIComponent(category)}`;
            return (
              <Link
                key={category}
                to={to}
                className="group flex-shrink-0 w-44 sm:w-52 lg:w-60 snap-start relative rounded-xl overflow-hidden bg-tbc-navy/60 ring-1 ring-white/10 hover:ring-tbc-gold transition-all"
                data-testid={`category-rail-${category.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
              >
                <div className="aspect-[4/5] relative">
                  <img
                    src={CATEGORY_IMAGES[category]}
                    alt={category}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy via-tbc-navy/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-heading text-base sm:text-lg leading-tight">
                      {category}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
